import type { ConnectingWallet } from '@/src/hooks/wallet/use-wallet-options'
import { Button } from '../ui/button'
import { WalletState } from './wallet-states'

interface CurrentConnectionWalletStateProps {
  className?: string
  walletIcon: string | undefined
  walletName: string
  totalEcosystemCount?: number
  connectedEcosystemCount?: number
  status: ConnectingWallet['status']
  statusCopy?: Partial<Record<ConnectingWallet['status'], string>>
  errorType: NonNullable<ConnectingWallet['error']>['type'] | undefined
  titleClassName?: string
  onRetry?: () => Promise<void>
}

export const CurrentConnectionWalletState = ({
  className,
  onRetry,
  errorType,
  status,
  statusCopy,
  walletIcon,
  walletName,
  totalEcosystemCount,
  connectedEcosystemCount,
  titleClassName,
}: CurrentConnectionWalletStateProps) => {
  if (status === 'error' && errorType === 'rejected') {
    return (
      <WalletState
        className={className}
        walletLogo={typeof walletIcon === 'string' ? walletIcon : undefined}
        title='You are not connected'
        titleClassName={titleClassName}
        totalEcosystemCount={totalEcosystemCount}
        connectedEcosystemCount={connectedEcosystemCount}
        desc={`You rejected the request to connect your ${walletName} browser extension.`}
        walletName={walletName}
        actions={
          onRetry ? (
            <div className='w-full pt-8'>
              <Button
                className='w-full'
                size='lg'
                variant='primary'
                onClick={onRetry}
              >
                Retry
              </Button>
            </div>
          ) : undefined
        }
        state='error'
        errorType='rejected'
      />
    )
  }

  if (status === 'error' && errorType !== 'rejected') {
    return (
      <WalletState
        className={className}
        walletLogo={typeof walletIcon === 'string' ? walletIcon : undefined}
        title='Uh oh, something went wrong'
        titleClassName={titleClassName}
        totalEcosystemCount={totalEcosystemCount}
        connectedEcosystemCount={connectedEcosystemCount}
        desc={
          errorType === 'connection-already-in-progress'
            ? 'Wallet connection is already in progress'
            : 'Sorry for the inconvenience. Please try again later.'
        }
        walletName={walletName}
        actions={
          onRetry ? (
            <div className='w-full px-1 py-4'>
              <Button size='lg' className='w-full' onClick={onRetry}>
                Retry
              </Button>
            </div>
          ) : undefined
        }
        state='error'
        errorType={errorType}
      />
    )
  }

  if (status === 'connecting') {
    return (
      <WalletState
        className={className}
        walletLogo={typeof walletIcon === 'string' ? walletIcon : undefined}
        walletName={walletName}
        title={`Approve in ${walletName}`}
        titleClassName={titleClassName}
        totalEcosystemCount={totalEcosystemCount}
        connectedEcosystemCount={connectedEcosystemCount}
        desc={
          statusCopy?.[status] ??
          `Open your ${walletName} browser extension to connect your wallet to Polaris.`
        }
        state='connecting'
      />
    )
  }

  return null
}
